import { auth } from '@/store/modules';
import config from '@/config';
import { injectable } from 'inversify';
import Axios from 'axios';
import { IApiService, ApiRequestConfig } from '@/interfaces/Services';

@injectable()
export default class ApiService implements IApiService {
  /**
   * Send get request to the given URL and return parsed response if successful
   * @param path
   * @param opts
   */
  public async get<T>(path: string, opts?: ApiRequestConfig): Promise<T> {
    return this.request(path, {
      ...opts,
      method: 'GET'
    })
  }

  /**
   * Send patch request to server and return parsed response if successful
   * @param path
   * @param data
   * @param opts
   */
  async patch<T>(path: string, data?: object, opts?: ApiRequestConfig): Promise<T> {
    return this.request(path, {
      ...opts,
      data: data,
      hasJsonData: true,
      method: 'PATCH'
    });
  }

  /**
   * Send post request to server and return parsed response if succesful
   * @param path
   * @param body
   * @param opts
   */
  async post<T>(path: string, body?: object, opts?: ApiRequestConfig): Promise<T> {
    return this.request(path, {
      ...opts,
      data: body,
      hasJsonData: true,
      method: 'POST'
    });
  }

  /**
   * Send request and if the status code is in the 4xx or 5xx range, error message will
   * be shown.
   * @param path
   * @param opts
   */
  async request<T>(path: string, opts?: ApiRequestConfig): Promise<T> {
    const url = `${config.apiUrl}${path}`;
    const preparedOpts = {
      ...opts,
      headers: { ...this.getRequestHeaders(opts || {}) },
    };

    const result = await Axios({ ...preparedOpts, url });

    if (result.status < 400) {
      return result.data || true;
    }

    /** @todo display error message BUT NOT IN ALERT!!!! */
    throw new Error(`Error ${result.data.status}: ${result.data.message}`);
  }

  /**
   * Get headers inclusing auth token and authorized center while keeping
   * the original headers.
   * @param opts
   */
  private getRequestHeaders(opts: ApiRequestConfig) {
    opts.headers = opts.headers || {};
    if (opts.hasJsonData === true) {
      opts.headers['Content-Type'] = 'application/json';
    }

    if (opts.skipHeaders) {
      return opts.headers;
    }
    const headers: { [key: string]: string } = {};
    if (auth.isAuthenticated) {
      headers[`Authorization`] = `Bearer ${auth.token}`;
      if (!opts.skipCenterHeader && auth.currentCenterId) {
        headers[`AUTH_CENTER`] = `${opts.overrideCenterId || auth.currentCenterId}`;
      }
    }

    return { ...headers, ...(opts.headers || {}) };
  }
}
